import React from 'react';
import { useT } from 'hooks/Locale/useT';
import { Form, DatePicker, Checkbox, FormInstance } from 'antd';
import moment from 'moment';
import { Input, Select } from 'components/Form';

interface FeatureProps {
    form: FormInstance;
    formItemLayout: { labelCol: { span: number }; wrapperCol: { span: number } };
    servicesLoading: boolean;
    serviceList: Record<number, string>;
    setServiceId: (serviceId: number) => void;
    disabledDates: (current: moment.Moment, to?: boolean) => boolean;
    setValidFromDate: (value: moment.Moment | null) => void;
    setValidToDate: (value: moment.Moment | null) => void;
    indefinite: boolean;
    setIndefinite: (indefinite: boolean) => void;
    edit: boolean;
}

// CSV-1964: licence-scoped feature toggles (e.g. the CERT_END early-completion cap) reuse the
// E_LEARNING_CONTENT service picker plus the APPEARANCE_SERVICE date/indefinite fields - a
// FEATURE service carries no settings of its own beyond which feature and how long it is granted.
const Feature: React.FC<FeatureProps> = ({
    form,
    formItemLayout,
    servicesLoading,
    serviceList,
    setServiceId,
    disabledDates,
    setValidFromDate,
    setValidToDate,
    indefinite,
    setIndefinite,
    edit,
}) => {
    const t = useT();

    const handleCheckboxChange = (event: any) => {
        const checked = event.target.checked;

        setIndefinite(checked);
        form.setFieldsValue({ indefinite: event.target.checked });

        if (checked) {
            setValidToDate(null);
            form.setFieldsValue({ validTo: null });
        }
    };

    return (
        <>
            {!edit ? (
                <Select
                    name="service"
                    label={t('licensing.service')}
                    validation={{ required: true }}
                    loading={servicesLoading}
                    manualOptions={serviceList}
                    onChange={(value: any) => setServiceId(parseInt(value))}
                    customLayout={formItemLayout}
                />
            ) : (
                <Input
                    name="service"
                    label={t('licensing.service')}
                    readOnly
                    customLayout={formItemLayout}
                />
            )}
            <Form.Item
                name="validFrom"
                label={t('licensing.valid_from')}
                rules={[{ required: true, message: t('validation.field_required') }]}
                {...formItemLayout}
            >
                <DatePicker
                    format="DD/MM/YYYY"
                    disabledDate={(current: any) => disabledDates(current)}
                    placeholder=""
                    onChange={(value: any) => setValidFromDate(value)}
                />
            </Form.Item>
            <Form.Item
                name="validTo"
                label={t('licensing.valid_to')}
                rules={[{ required: !indefinite, message: t('validation.field_required') }]}
                {...formItemLayout}
            >
                <DatePicker
                    format="DD/MM/YYYY"
                    disabledDate={(current: any) => disabledDates(current, true)}
                    placeholder=""
                    onChange={(value: any) => setValidToDate(value)}
                    disabled={indefinite}
                />
            </Form.Item>
            <Form.Item name="indefinite" label={t('general.indefinite')} {...formItemLayout}>
                <Checkbox onChange={(event: any) => handleCheckboxChange(event)} checked={indefinite} />
            </Form.Item>
        </>
    );
};

export default Feature;
